import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccessRequestItemStatus,
  AdminAccessRequest,
  AdminDashboard,
  CollectionMeta,
  MenuCatalogEntry,
  ReviewAccessRequestItemInput,
  TenantAuditEntry,
  TenantCounts,
  TenantDetail,
  TenantListQuery,
  TenantMember,
  TenantSummary,
  UpdateTenantInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['admin', 'tenants'] as const;
const ACCESS_REQUESTS_KEY = ['admin', 'access-requests'] as const;

export interface TenantPage {
  items: TenantSummary[];
  meta: CollectionMeta;
}

function toQuery(q: TenantListQuery): Record<string, string | number | undefined> {
  return {
    page: q.page,
    pageSize: q.pageSize,
    status: q.status,
    search: q.search,
    email: q.email,
    industry: q.industry,
    createdFrom: q.createdFrom,
    createdTo: q.createdTo,
    sortBy: q.sortBy,
    sortOrder: q.sortOrder,
  };
}

export async function listTenants(q: TenantListQuery = {}): Promise<TenantPage> {
  const res = await apiClient.getCollection<TenantSummary>('/admin/tenants', { query: toQuery(q) });
  return { items: res.data, meta: res.meta };
}
export const getTenant = (id: string) => apiClient.get<TenantDetail>(`/admin/tenants/${id}`);
export const updateTenant = (id: string, patch: UpdateTenantInput) =>
  apiClient.patch<TenantDetail>(`/admin/tenants/${id}`, patch);
export const listTenantMembers = (id: string) =>
  apiClient.getCollection<TenantMember>(`/admin/tenants/${id}/users`);
export const listTenantAudit = (id: string) =>
  apiClient.getCollection<TenantAuditEntry>(`/admin/tenants/${id}/audit`);

export function useTenants(query: TenantListQuery = {}) {
  return useQuery<TenantPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listTenants(query),
    placeholderData: keepPreviousData,
  });
}

/** All / per-status tenant counts for the grid tabs, under the same filters (status ignored). */
export function useTenantCounts(query: TenantListQuery) {
  const { page: _p, pageSize: _ps, sortBy: _s, sortOrder: _o, status: _st, ...filters } = query;
  return useQuery<TenantCounts, ApiClientError>({
    queryKey: [...KEY, 'counts', filters],
    queryFn: () =>
      apiClient.get<TenantCounts>('/admin/tenants/counts', { query: toQuery(filters) }),
    placeholderData: keepPreviousData,
  });
}

export function useTenant(id: string | undefined) {
  return useQuery<TenantDetail, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getTenant(id!),
    enabled: Boolean(id),
  });
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation<TenantDetail, ApiClientError, { id: string; patch: UpdateTenantInput }>({
    mutationFn: ({ id, patch }) => updateTenant(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useTenantMembers(id: string | undefined) {
  return useQuery<TenantMember[], ApiClientError>({
    queryKey: [...KEY, 'members', id],
    queryFn: async () => (await listTenantMembers(id!)).data,
    enabled: Boolean(id),
  });
}

export function useTenantAudit(id: string | undefined) {
  return useQuery<TenantAuditEntry[], ApiClientError>({
    queryKey: [...KEY, 'audit', id],
    queryFn: async () => (await listTenantAudit(id!)).data,
    enabled: Boolean(id),
  });
}

export interface AdminAccessRequestPage {
  items: AdminAccessRequest[];
  meta: CollectionMeta;
}

export async function listAdminAccessRequests(
  status?: AccessRequestItemStatus,
): Promise<AdminAccessRequestPage> {
  const res = await apiClient.getCollection<AdminAccessRequest>('/admin/access-requests', {
    query: { status, pageSize: 100 },
  });
  return { items: res.data, meta: res.meta };
}

export const reviewAccessRequestItem = (
  requestId: string,
  itemId: string,
  input: ReviewAccessRequestItemInput,
) => apiClient.post<null>(`/admin/access-requests/${requestId}/items/${itemId}/review`, input);

export function useAdminAccessRequests(status?: AccessRequestItemStatus) {
  return useQuery<AdminAccessRequestPage, ApiClientError>({
    queryKey: [...ACCESS_REQUESTS_KEY, status],
    queryFn: () => listAdminAccessRequests(status),
  });
}

export function useReviewAccessRequestItem() {
  const qc = useQueryClient();
  return useMutation<
    null,
    ApiClientError,
    { requestId: string; itemId: string; input: ReviewAccessRequestItemInput }
  >({
    mutationFn: ({ requestId, itemId, input }) => reviewAccessRequestItem(requestId, itemId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCESS_REQUESTS_KEY }),
  });
}

export const getTenantMenuAccess = async (tenantId: string): Promise<MenuCatalogEntry[]> =>
  (await apiClient.getCollection<MenuCatalogEntry>(`/admin/tenants/${tenantId}/menu-access`)).data;

export const setTenantMenuAccess = (tenantId: string, menuKeys: string[]) =>
  apiClient.put<MenuCatalogEntry[]>(`/admin/tenants/${tenantId}/menu-access`, { menuKeys });

export function useTenantMenuAccess(tenantId: string | undefined) {
  return useQuery<MenuCatalogEntry[], ApiClientError>({
    queryKey: [...KEY, 'menu-access', tenantId],
    queryFn: () => getTenantMenuAccess(tenantId!),
    enabled: Boolean(tenantId),
  });
}

export function useSetTenantMenuAccess() {
  const qc = useQueryClient();
  return useMutation<MenuCatalogEntry[], ApiClientError, { tenantId: string; menuKeys: string[] }>({
    mutationFn: ({ tenantId, menuKeys }) => setTenantMenuAccess(tenantId, menuKeys),
    onSuccess: (_data, { tenantId }) =>
      qc.invalidateQueries({ queryKey: [...KEY, 'menu-access', tenantId] }),
  });
}

/** Drop a workspace's admin overrides - exactly its plan's menus. */
export function useResetTenantMenuAccess() {
  const qc = useQueryClient();
  return useMutation<MenuCatalogEntry[], ApiClientError, string>({
    mutationFn: async (tenantId) =>
      (
        await apiClient.getCollection<MenuCatalogEntry>(
          `/admin/tenants/${tenantId}/menu-access/reset`,
          { method: 'POST' },
        )
      ).data,
    onSuccess: (_data, tenantId) =>
      qc.invalidateQueries({ queryKey: [...KEY, 'menu-access', tenantId] }),
  });
}

export const getAdminDashboard = () => apiClient.get<AdminDashboard>('/admin/dashboard');

export function useAdminDashboard() {
  return useQuery<AdminDashboard, ApiClientError>({
    queryKey: ['admin', 'dashboard'],
    queryFn: getAdminDashboard,
  });
}
